/**
 * Security configuration for Passport
 *
 * Split into 3 independent config objects (SRP):
 * - SessionConfig: Session timeout and inactivity handling
 * - CsrfConfig: CSRF token protection
 * - HttpsConfig: HTTPS enforcement
 */

/**
 * Session timeout configuration
 */
export interface SessionConfig {
  timeoutMs: number;
  warningMs: number;
}

/**
 * CSRF protection configuration
 */
export interface CsrfConfig {
  enabled: boolean;
  headerName: string;
}

/**
 * HTTPS enforcement configuration
 */
export interface HttpsConfig {
  enforceHttpsOnly: boolean;
}

const getEnvVar = (key: string, defaultValue?: string): string => {
  const value = import.meta.env[`VITE_${key}`];
  if (!value && defaultValue === undefined && import.meta.env.DEV) {
    console.warn(`Environment variable VITE_${key} not found, using default`);
  }
  return value || defaultValue || '';
};

const parsePositiveInteger = (value: string, fieldName: string): number => {
  const parsed = parseInt(value, 10);
  if (isNaN(parsed) || parsed <= 0) {
    throw new Error(`${fieldName} must be a positive integer, got: ${value}`);
  }
  return parsed;
};

const parseBoolean = (value: string): boolean => {
  if (!value || value === '0' || value.toLowerCase() === 'false') {
    return false;
  }
  return true;
};

const validateSessionConfig = (config: SessionConfig): void => {
  if (config.warningMs >= config.timeoutMs) {
    throw new Error(
      `Session warning time (${config.warningMs}ms) must be less than timeout time (${config.timeoutMs}ms)`
    );
  }
};

/**
 * Session timeout configuration
 * Validates that warning comes before timeout
 */
export const sessionConfig: SessionConfig = (() => {
  const timeoutMinutes = parsePositiveInteger(
    getEnvVar('SESSION_TIMEOUT_MINUTES', '15'),
    'SESSION_TIMEOUT_MINUTES'
  );
  const warningMinutes = parsePositiveInteger(
    getEnvVar('SESSION_WARNING_MINUTES', '2'),
    'SESSION_WARNING_MINUTES'
  );

  const config: SessionConfig = {
    timeoutMs: timeoutMinutes * 60 * 1000,
    warningMs: warningMinutes * 60 * 1000,
  };

  validateSessionConfig(config);
  return config;
})();

/**
 * CSRF protection configuration
 */
export const csrfConfig: CsrfConfig = {
  enabled: parseBoolean(getEnvVar('CSRF_ENABLED', 'true')),
  headerName: getEnvVar('CSRF_HEADER_NAME', 'X-CSRF-Token').trim(),
};

/**
 * HTTPS enforcement configuration
 */
export const httpsConfig: HttpsConfig = {
  enforceHttpsOnly: parseBoolean(getEnvVar('ENABLE_HTTPS_ONLY', 'false')),
};

/**
 * Legacy export for backward compatibility
 * (some code might import securityConfig directly)
 */
export const securityConfig = {
  sessionTimeoutMs: sessionConfig.timeoutMs,
  sessionWarningMs: sessionConfig.warningMs,
  csrfEnabled: csrfConfig.enabled,
  csrfHeaderName: csrfConfig.headerName,
  csrfStorageKey: 'x-csrf-token',
  httpsOnly: httpsConfig.enforceHttpsOnly,
};
